export interface OpenAIModelPricing {
  provider: "openai";
  model: string;
  currency: "USD";
  inputUsdPerMillion: number;
  cachedInputUsdPerMillion: number;
  outputUsdPerMillion: number;
  sourceUrl: string;
  effectiveDate: string;
}

export interface NormalizedOpenAIUsage {
  inputTokens: number;
  cachedInputTokens: number;
  uncachedInputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  totalTokens: number;
}

export interface OpenAITokenSpend {
  provider: "openai";
  model: string;
  inputTokens: number;
  cachedInputTokens: number;
  uncachedInputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  totalTokens: number;
  amountMicros: number;
  currency: "USD";
  inputPricePerMillionMicros: number;
  cachedInputPricePerMillionMicros: number;
  outputPricePerMillionMicros: number;
  pricingSource: string;
  pricingEffectiveAt: string;
  usage: NormalizedOpenAIUsage;
}

const OPENAI_PRICING_SOURCE_URL =
  "https://developers.openai.com/api/docs/models/gpt-5.4/";
const OPENAI_PRICING_EFFECTIVE_DATE = "2026-05-19";

export const OPENAI_MODEL_PRICING_USD_PER_MILLION: Record<
  string,
  OpenAIModelPricing
> = {
  "gpt-5.4": {
    provider: "openai",
    model: "gpt-5.4",
    currency: "USD",
    inputUsdPerMillion: 2.5,
    cachedInputUsdPerMillion: 0.25,
    outputUsdPerMillion: 15,
    sourceUrl: OPENAI_PRICING_SOURCE_URL,
    effectiveDate: OPENAI_PRICING_EFFECTIVE_DATE,
  },
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : null;
}

function numberField(record: Record<string, unknown> | null, key: string) {
  const value = record?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function microsPerMillion(usdPerMillion: number): number {
  return Math.round(usdPerMillion * 1_000_000);
}

export function getOpenAIModelPricing(
  model: string,
): OpenAIModelPricing | null {
  return OPENAI_MODEL_PRICING_USD_PER_MILLION[model] || null;
}

export function normalizeOpenAIUsage(rawUsage: unknown): NormalizedOpenAIUsage {
  const usage = asRecord(rawUsage);
  if (!usage) {
    return {
      inputTokens: 0,
      cachedInputTokens: 0,
      uncachedInputTokens: 0,
      outputTokens: 0,
      reasoningTokens: 0,
      totalTokens: 0,
    };
  }

  const inputTokens =
    numberField(usage, "prompt_tokens") || numberField(usage, "input_tokens");
  const outputTokens =
    numberField(usage, "completion_tokens") ||
    numberField(usage, "output_tokens");
  const totalTokens =
    numberField(usage, "total_tokens") || inputTokens + outputTokens;
  const inputDetails =
    asRecord(usage.prompt_tokens_details) ||
    asRecord(usage.input_tokens_details);
  const outputDetails =
    asRecord(usage.completion_tokens_details) ||
    asRecord(usage.output_tokens_details);
  const cachedInputTokens = Math.min(
    inputTokens,
    numberField(inputDetails, "cached_tokens") ||
      numberField(inputDetails, "cached_input_tokens"),
  );
  const reasoningTokens =
    numberField(outputDetails, "reasoning_tokens") ||
    numberField(outputDetails, "reasoning_output_tokens");

  return {
    inputTokens,
    cachedInputTokens,
    uncachedInputTokens: Math.max(inputTokens - cachedInputTokens, 0),
    outputTokens,
    reasoningTokens,
    totalTokens,
  };
}

export function calculateOpenAITokenSpend(
  model: string,
  rawUsage: unknown,
): OpenAITokenSpend | null {
  const pricing = getOpenAIModelPricing(model);
  if (!pricing) return null;

  const usage = normalizeOpenAIUsage(rawUsage);
  const inputPricePerMillionMicros = microsPerMillion(
    pricing.inputUsdPerMillion,
  );
  const cachedInputPricePerMillionMicros = microsPerMillion(
    pricing.cachedInputUsdPerMillion,
  );
  const outputPricePerMillionMicros = microsPerMillion(
    pricing.outputUsdPerMillion,
  );
  const amountMicros = Math.round(
    (usage.uncachedInputTokens * inputPricePerMillionMicros +
      usage.cachedInputTokens * cachedInputPricePerMillionMicros +
      usage.outputTokens * outputPricePerMillionMicros) /
      1_000_000,
  );

  return {
    provider: "openai",
    model,
    inputTokens: usage.inputTokens,
    cachedInputTokens: usage.cachedInputTokens,
    uncachedInputTokens: usage.uncachedInputTokens,
    outputTokens: usage.outputTokens,
    reasoningTokens: usage.reasoningTokens,
    totalTokens: usage.totalTokens,
    amountMicros,
    currency: "USD",
    inputPricePerMillionMicros,
    cachedInputPricePerMillionMicros,
    outputPricePerMillionMicros,
    pricingSource: pricing.sourceUrl,
    pricingEffectiveAt: pricing.effectiveDate,
    usage,
  };
}
